<template>
  <div class="file-browser">
    <div class="container">
      <div class="page-header">
        <h1>Explorador de Archivos</h1>
        <p>Explora los archivos almacenados en el bucket de tu país: <strong>{{ bucketName }}</strong></p>
      </div>

      <Card class="content-card">
        <template #content>
          <Breadcrumb :model="breadcrumbItems" home="pi pi-home" />
          
          <div class="browser-actions p-mb-3">
            <div class="current-path">
              <span class="pi pi-folder-open" style="margin-right: 8px;"></span>
              <span v-if="currentFolder === ''">Raíz del Bucket</span>
              <span v-else>{{ currentFolder }}</span>
            </div>
            <Button 
              v-if="currentFolder !== ''" 
              icon="pi pi-arrow-left" 
              label="Volver" 
              class="p-button-outlined p-mr-2"
              @click="goToParentFolder" 
            />
          </div>

          <ProgressBar v-if="loading" mode="indeterminate" style="height: 6px" />

          <DataTable 
            :value="fileList" 
            :loading="loading"
            sortField="type" 
            :sortOrder="1"
            :paginator="fileList.length > 10" 
            :rows="10"
            stripedRows
            dataKey="path"
            responsiveLayout="scroll"
            class="p-mt-3"
          >
            <Column field="type" header="Tipo" :sortable="true" style="width: 80px">
              <template #body="slotProps">
                <span v-if="slotProps.data.isFolder" class="pi pi-folder" style="font-size: 1.5rem; color: var(--primary-color);"></span>
                <span v-else class="pi pi-file" style="font-size: 1.5rem; color: var(--text-color-secondary);"></span>
              </template>
            </Column>
            
            <Column field="name" header="Nombre" :sortable="true">
              <template #body="slotProps">
                <div class="file-name-cell" @click="handleItemClick(slotProps.data)">
                  <span>{{ slotProps.data.name }}</span>
                </div>
              </template>
            </Column>
            
            <Column field="size" header="Tamaño" :sortable="true">
              <template #body="slotProps">
                <span v-if="slotProps.data.isFolder">—</span>
                <span v-else>{{ formatFileSize(slotProps.data.size) }}</span>
              </template>
            </Column>
            
            <Column field="lastModified" header="Última modificación" :sortable="true">
              <template #body="slotProps">
                <span v-if="slotProps.data.isFolder">—</span>
                <span v-else>{{ formatDate(slotProps.data.lastModified) }}</span>
              </template>
            </Column>
            
            <Column header="Acciones">
              <template #body="slotProps">
                <Button v-if="!slotProps.data.isFolder" 
                  icon="pi pi-download" 
                  class="p-button-rounded p-button-text"
                  @click="downloadFile(slotProps.data)" 
                  tooltip="Descargar"
                />
              </template>
            </Column>
          </DataTable>

          <div v-if="fileList.length === 0 && !loading" class="empty-state p-mt-4">
            <i class="pi pi-folder-open empty-icon"></i>
            <h3>Carpeta vacía</h3>
            <p>No hay archivos en esta ubicación</p>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'

export default {
  name: 'FileBrowser',
  setup() {
    const store = useStore()
    const loading = ref(false)
    const fileList = ref([])
    const currentFolder = ref('')
    const bucketName = ref('')
    const folderHistory = ref([])

    const breadcrumbItems = computed(() => {
      const items = [{ label: 'Inicio', to: '/dashboard' }]
      
      if (currentFolder.value === '') {
        items.push({ label: 'Explorador de Archivos', to: '/browse' })
      } else {
        items.push({ label: 'Explorador de Archivos', to: '/browse' })
        
        // Añadir cada nivel de carpeta a las migas de pan
        const folderParts = currentFolder.value.split('/')
        let currentPath = ''
        
        folderParts.forEach((part) => {
          if (part) {
            currentPath += (currentPath ? '/' : '') + part
            items.push({
              label: part,
              command: () => navigateToFolder(currentPath)
            })
          }
        })
      }
      
      return items
    })

    // Cargar la lista de archivos y carpetas
    const loadFileList = async (folder = '') => {
      loading.value = true
      
      try {
        const response = await axios.get(`/api/v1/files/bucket`, {
          params: { folder }
        })
        
        if (response.data?.bucket_name) {
          bucketName.value = response.data.bucket_name
        }
        
        // Transformar los datos para la DataTable
        fileList.value = []
        
        // Primero agregar las carpetas
        if (response.data?.folders) {
          fileList.value = response.data.folders.map(folder => ({
            name: folder.name,
            path: folder.path,
            isFolder: true,
            type: 'folder'
          }))
        }
        
        // Luego agregar los archivos
        if (response.data?.files) {
          const files = response.data.files.map(file => ({
            name: file.name,
            path: file.path,
            size: file.size,
            lastModified: file.last_modified,
            isFolder: false,
            type: 'file'
          }))
          
          fileList.value = [...fileList.value, ...files]
        }
      } catch (error) {
        console.error('Error al cargar archivos:', error)
        store.dispatch('notify', {
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudieron cargar los archivos',
          life: 5000
        })
      } finally {
        loading.value = false
      }
    }

    // Navegar a una carpeta
    const navigateToFolder = (folderPath) => {
      // Si ya estamos en una carpeta, guardar en el historial
      if (currentFolder.value !== '') {
        folderHistory.value.push(currentFolder.value)
      }
      
      currentFolder.value = folderPath
      loadFileList(folderPath)
    }

    // Volver a la carpeta superior
    const goToParentFolder = () => {
      if (folderHistory.value.length > 0) {
        // Recuperar la última carpeta del historial
        const previousFolder = folderHistory.value.pop()
        currentFolder.value = previousFolder
        loadFileList(previousFolder)
      } else {
        // Volver a la raíz
        currentFolder.value = ''
        loadFileList('')
      }
    }

    // Manejar el clic en un elemento (archivo o carpeta)
    const handleItemClick = (item) => {
      if (item.isFolder) {
        navigateToFolder(item.path)
      }
    }

    // Descargar un archivo
    const downloadFile = async (file) => {
      try {
        const response = await axios.get(`/api/v1/files/download`, {
          params: { file_path: file.path },
          responseType: 'blob'
        })
        
        // Crear un enlace para descargar el archivo
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', file.name)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('Error al descargar archivo:', error)
        store.dispatch('notify', {
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo descargar el archivo',
          life: 5000
        })
      }
    }

    // Formatear el tamaño de archivo
    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes'
      
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    // Formatear fecha
    const formatDate = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleString('es-ES')
    }

    onMounted(() => {
      loadFileList()
    })

    return {
      loading,
      fileList,
      currentFolder,
      bucketName,
      breadcrumbItems,
      navigateToFolder,
      goToParentFolder,
      handleItemClick,
      downloadFile,
      formatFileSize,
      formatDate
    }
  }
}
</script>

<style scoped>
.file-browser {
  background-color: var(--background-color);
  min-height: calc(100vh - 180px);
}

.page-header {
  margin-bottom: 20px;
}

.page-header h1 {
  margin-bottom: 5px;
  font-size: 1.8rem;
  color: var(--text-color);
}

.page-header p {
  margin: 0;
  color: var(--text-color-secondary);
}

.content-card {
  margin-bottom: 30px;
}

.browser-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 15px 0;
  padding: 12px;
  background-color: var(--surface-ground);
  border-radius: 6px;
}

.current-path {
  font-weight: 500;
}

.file-name-cell {
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  display: inline-block;
}

.file-name-cell:hover {
  background-color: var(--surface-hover);
}

.empty-state {
  text-align: center;
  padding: 40px 0;
  color: var(--text-color-secondary);
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 10px;
}
</style> 